import React, { useState, useEffect, useCallback, useRef } from "react";
import { useUserContext } from "../config/UserContext";
import { useKeycloak } from "../config/KeycloakContext";
import { useLoadScript } from "@react-google-maps/api";

const libraries: any = ['places'];

function useNewPlacesAutocomplete(selectedCountry: string) {
    const [value, setValue] = useState('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const sessionTokenRef = useRef<any>(null);

    const initToken = () => {
        if (!sessionTokenRef.current && window.google?.maps?.places?.AutocompleteSessionToken) {
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }
    };

    const fetchSuggestions = useCallback(async (input: string) => {
        setValue(input);
        if (!input) {
            setSuggestions([]);
            return;
        }

        if (!window.google?.maps?.places?.AutocompleteSuggestion) {
            return;
        }

        initToken();
        setLoading(true);

        try {
            const request: any = {
                input,
                sessionToken: sessionTokenRef.current,
            };
            
            if (selectedCountry) {
                request.includedRegionCodes = [selectedCountry.toLowerCase()];
            }

            const response = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
            setSuggestions(response.suggestions || []);
        } catch (error) {
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, [selectedCountry]);

    const clearSuggestions = () => setSuggestions([]);
    const resetToken = () => { sessionTokenRef.current = null; };

    return { value, setValue, suggestions, fetchSuggestions, clearSuggestions, resetToken, loading };
}

interface DocumentDetailsCardProps {
  onContinue: () => void;
}

const DocumentDetailsCard: React.FC<DocumentDetailsCardProps> = ({ onContinue }) => {
  const { county, document, apiBaseUrl } = useUserContext();
  const { getToken } = useKeycloak();
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const customFields = county?.documentSchema?.customDocumentFields || [];

  const { isLoaded } = useLoadScript({
      googleMapsApiKey: import.meta.env.VITE_GOOGLE_PLACES_API_KEY || '',
      libraries,
      version: "weekly"
  });

  const {
      value: addressValue,
      setValue: setAddressValue,
      suggestions: addressSuggestions,
      fetchSuggestions: fetchAddressSuggestions,
      clearSuggestions: clearAddressSuggestions,
  } = useNewPlacesAutocomplete('US');

  // Find if there is an address field to bind the Google input
  const addressField = customFields.find((f: any) => f.type === 'address');

  useEffect(() => {
    if (addressField && addressValue !== formData[addressField.key]) {
       setFormData(prev => ({ ...prev, [addressField.key]: addressValue }));
    }
  }, [addressValue, addressField]);

  const handleAddressSelect = async (suggestion: any) => {
      const formatted = (suggestion.placePrediction?.text?.text || '').replace(/,\s*USA$/, '').replace(/\s*USA$/, '').trim();
      setAddressValue(formatted);
      if (addressField) {
          setFormData(prev => ({ ...prev, [addressField.key]: formatted }));
      }
      clearAddressSuggestions();
  };

  // Initialize with any existing attributes? (Optional: we could fetch them, but for creation flow we just collect them)
  
  const handleInputChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!document?.lookup) return;
    
    // Validate required fields
    let hasError = false;
    for (const field of customFields) {
      if (field.required && !formData[field.key]) {
        setError(`Please fill out all required fields: ${field.label}`);
        hasError = true;
        break;
      }
    }
    if (hasError) return;

    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");

      // Save each attribute individually
      for (const field of customFields) {
        if (formData[field.key]) {
          const res = await fetch(`${apiBaseUrl}/api/documents/${document.lookup}/attributes`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              key: field.key,
              value: formData[field.key]
            })
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.message || 'Failed to save attribute');
          }
        }
      }
      
      setSaving(false);
      onContinue();
    } catch (err: any) {
      setError(err.message || "Failed to save document details");
      setSaving(false);
    }
  };

  if (!customFields || customFields.length === 0) {
    // If no custom fields, automatically continue immediately
    useEffect(() => {
      onContinue();
    }, []);
    return null;
  }

  return (
    <form className="w-full flex flex-col gap-6" onSubmit={handleSubmit} autoComplete="off">
      {customFields.map((field: any) => (
        <div key={field.key} className="flex flex-col gap-2 w-full">
          <label className="text-[#485D7D] text-[14px] font-semibold font-plus-jakarta" htmlFor={`field-${field.key}`}>
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          {field.description && (
            <p className="text-sm text-[#7C8B9D] mb-1">{field.description}</p>
          )}
          {field.type === 'textarea' ? (
            <textarea
              id={`field-${field.key}`}
              className="w-full px-3 py-2 rounded border border-[#D7DFEC] bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none focus:border-[#0B4BB6]"
              rows={4}
              value={formData[field.key] || ''}
              onChange={e => handleInputChange(field.key, e.target.value)}
              required={field.required}
            />
          ) : field.type === 'address' ? (
            <div className="relative">
              <input
                id={`field-${field.key}`}
                autoComplete="new-password"
                className="w-full px-3 py-2 rounded border border-[#D7DFEC] bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none focus:border-[#0B4BB6]"
                value={addressValue || formData[field.key] || ''}
                onChange={e => fetchAddressSuggestions(e.target.value)}
                placeholder="Start typing an address..."
                required={field.required}
              />
              {addressSuggestions.length > 0 && (
                <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {addressSuggestions.map((suggestion: any, index: number) => (
                        <li
                            key={suggestion.placePrediction.placeId || index}
                            onMouseDown={e => {
                                e.preventDefault();
                                handleAddressSelect(suggestion);
                            }}
                            className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-700 hover:bg-gray-50"
                        >
                            <span className="block truncate">{suggestion.placePrediction.text.text}</span>
                        </li>
                    ))}
                </ul>
              )}
            </div>
          ) : (
            <input
              id={`field-${field.key}`}
              type={field.type || 'text'}
              className="w-full px-3 py-2 rounded border border-[#D7DFEC] bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none focus:border-[#0B4BB6]"
              value={formData[field.key] || ''}
              onChange={e => handleInputChange(field.key, e.target.value)}
              required={field.required}
            />
          )}
        </div>
      ))}
      
      {error && <div className="text-red-500 text-sm">{error}</div>}

      <button
        type="submit"
        className={`w-full bg-[#0B4BB6] text-white font-bold text-[15px] font-plus-jakarta py-2.5 rounded-full mt-4 transition-colors border-2 border-[#0B4BB6] ${saving ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[#23235a]'}`}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save & Continue'}
      </button>
    </form>
  );
};

export default DocumentDetailsCard;
